import { useState } from 'react';
import { Button } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useIntl } from 'react-intl';

import { calculateDensityMessages } from './calculate-density-messages';
import { RecommendedFreightClassModal } from './RecommendedFreightClassModal';
import { calculateDensity } from '../../api/ltl.api';
import { mapDimensionUnit, mapHandlingUnitsToPayload, mapWeightUnit } from '../../ltl/freight-payload-mappers';

import type { FreightDensityAction } from './RecommendedFreightClassModal';
import type { DimensionUnit } from '../../types/dimensions';
import type { FreightHandlingUnitDensityResult } from '../../types/freight-responses';
import type { LtlHandlingUnit } from '../../types/ltl';
import type { WeightUnit } from '../../types/weight';

export type CalculateDensityButtonProps = {
  unit: LtlHandlingUnit;
  index: number;
  sellerFreightProviderId?: number;
  displayWeightUnit?: WeightUnit;
  displayDimensionUnit?: DimensionUnit;
  onChange: (next: LtlHandlingUnit) => void;
  onDensityAction?: (action: FreightDensityAction) => void;
};

export const CalculateDensityButton = ({
  unit,
  index,
  sellerFreightProviderId,
  displayWeightUnit = 'Pounds',
  displayDimensionUnit = 'Inches',
  onChange,
  onDensityAction,
}: CalculateDensityButtonProps) => {
  const intl = useIntl();
  const [densityResult, setDensityResult] = useState<FreightHandlingUnitDensityResult | null>(null);
  const [isCalculatingDensity, setIsCalculatingDensity] = useState(false);
  const [isDensityModalOpen, { open: openDensityModal, close: closeDensityModal }] = useDisclosure(false);

  const handleCalculateDensity = async () => {
    setIsCalculatingDensity(true);
    try {
      const weightUnit = mapWeightUnit(unit.commodities[0]?.weightUnit ?? displayWeightUnit);
      const dimensionUnit = mapDimensionUnit(unit.dimensionUnit ?? displayDimensionUnit);
      const [mappedUnit] = mapHandlingUnitsToPayload([unit], weightUnit, dimensionUnit);
      const response = await calculateDensity({ sellerFreightProviderId: sellerFreightProviderId || null, handlingUnits: [mappedUnit] });
      setDensityResult(response.handlingUnitList?.[0] ?? null);
      openDensityModal();
    } catch {
      setDensityResult(null);
      openDensityModal();
    } finally {
      setIsCalculatingDensity(false);
    }
  };

  const handleApplyDensityRecommendations = (indices: number[]) => {
    if (!densityResult) return;
    const indexSet = new Set(indices);
    const updatedCommodities = unit.commodities.map((c, idx) => {
      if (!indexSet.has(idx)) return c;
      const rec = densityResult.commodities[idx]?.recommendedFreightClass;
      return rec ? { ...c, freightClass: rec } : c;
    });
    onChange({ ...unit, commodities: updatedCommodities });
  };

  return (
    <>
      <Button
        data-testid={`calculate-density-${index}`}
        loading={isCalculatingDensity}
        onClick={handleCalculateDensity}
        size='xs'
        variant='tertiary'
      >
        {intl.formatMessage(calculateDensityMessages.calculateDensity)}
      </Button>
      <RecommendedFreightClassModal
        commodities={unit.commodities}
        commodityResults={densityResult?.commodities ?? []}
        density={densityResult?.density?.value ?? null}
        densityUnit={densityResult?.density?.unit ?? 'lb/ft³'}
        onApply={handleApplyDensityRecommendations}
        onClose={closeDensityModal}
        onDensityAction={onDensityAction}
        opened={isDensityModalOpen}
      />
    </>
  );
};
